import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/CentralFundLedger.jsx
// HO-only view of the Central Fund ledger.
// Shows bank credits (funds loaded) and payment debits (bank payments approved),
// a per-branch cash breakdown, and a drill-down view of any branch's cash drawer.
// Branch users see an access-denied message; they never manage central funds.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Tag, Space, Button, Typography, Spin, Alert,
  Row, Col, Statistic, DatePicker, Select, Modal, Form, Input,
  Tooltip, Divider, message, Drawer, Empty,
} from 'antd';
import {
  ReloadOutlined, BankOutlined, LockOutlined, InfoCircleOutlined,
  EyeOutlined, ClearOutlined, ArrowUpOutlined, ArrowDownOutlined,
  ShopOutlined, WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchCentralFund, fundBranch, fetchCashSummary, fetchCashLedger } from '../api';
import { fetchFunds } from '../../accounting/api';
import DenominationCountForm from '../components/DenominationCountForm';
import { DENOMINATION_NOTES } from '../constants/treasuryConstants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const ENTRY_TYPE_CONFIG = {
  OPENING_BALANCE:      { color: 'blue',     label: 'Opening' },
  BANK_CREDIT:          { color: 'green',    label: 'Bank Credit' },
  PAYMENT_DEBIT:        { color: 'red',      label: 'Payment Debit' },
  PAYMENT_REVERSAL:     { color: 'cyan',     label: 'Payment Reversal' },
  BRANCH_FUNDING:       { color: 'purple',   label: 'Branch Funding (Cash)' },
  BRANCH_CASH_SPENT:    { color: 'volcano',  label: 'Branch Cash Spent' },
  BRANCH_CASH_RETURNED: { color: 'geekblue', label: 'Branch Cash Returned' },
  ADJUSTMENT:           { color: 'orange',   label: 'Adjustment' },
  CLOSING_BALANCE:      { color: 'default',  label: 'Closing' },
};

// Money coming IN to the HO bank / cash pool (green column).
const CREDIT_TYPES = ['OPENING_BALANCE', 'BANK_CREDIT', 'ADJUSTMENT', 'PAYMENT_REVERSAL', 'BRANCH_CASH_RETURNED'];
// Money going OUT of the HO bank / cash pool (red column).
const DEBIT_TYPES  = ['PAYMENT_DEBIT', 'CLOSING_BALANCE', 'BRANCH_FUNDING', 'BRANCH_CASH_SPENT'];

const ENTRY_TYPE_OPTIONS = Object.entries(ENTRY_TYPE_CONFIG).map(([k, v]) => ({
  value: k,
  label: v.label,
}));

const CASH_LEDGER_TYPE_CONFIG = {
  OPENING_BALANCE: { color: 'blue',    label: 'Opening' },
  CASH_IN:         { color: 'green',   label: 'Cash In' },
  CASH_OUT:        { color: 'red',     label: 'Cash Out' },
  ADJUSTMENT:      { color: 'orange',  label: 'Adjustment' },
  CLOSING_BALANCE: { color: 'default', label: 'Closing' },
};

/* ─── Resizable header cell (drag the right edge to widen a column) ─────────── */
function ResizableTitle({ onResize, width, ...restProps }) {
  const startRef = useRef(null);
  if (width === undefined || width === null) {
    return <th {...restProps} />;
  }

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startRef.current = { startX: e.clientX, startW: width };
    const onMove = (ev) => {
      const delta = ev.clientX - startRef.current.startX;
      onResize(Math.max(70, startRef.current.startW + delta));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <th {...restProps} style={{ ...(restProps.style || {}), position: 'relative' }}>
      {restProps.children}
      <span
        className="cfl-resize-handle"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      />
    </th>
  );
}

export default function CentralFundLedger() {
  const { user } = useAuth();

  const [isHO, setIsHO] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const [entries, setEntries] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [cashSummary, setCashSummary] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

  const [filters, setFilters] = useState({ entryType: null, dateRange: null, branchId: null });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Fund Branch modal
  const [fundModal, setFundModal] = useState(false);
  const [fundSubmitting, setFundSubmitting] = useState(false);
  const [fundDenominations, setFundDenominations] = useState({});
  const [fundForm] = Form.useForm();
  const [cashFunds, setCashFunds] = useState([]);

  // Branch cash drill-down drawer
  const [drawer, setDrawer] = useState({ open: false, branch: null });
  const [drawerData, setDrawerData] = useState({ entries: [], loading: false, cashIn: 0, cashOut: 0, balance: 0 });

  // Resizable column widths (main ledger table)
  const [colWidths, setColWidths] = useState({
    date: 110, reference: 140, type: 150, description: 260,
    bankRef: 130, branch: 150, credit: 130, debit: 130, balance: 150,
  });
  const handleResize = (key) => (newWidth) => {
    setColWidths((w) => ({ ...w, [key]: newWidth }));
  };

  // Load branches once (used by both the filter and the Fund Branch modal).
  const loadBranches = useCallback(() => {
    if (branches.length || branchesLoading) return;
    setBranchesLoading(true);
    fetchBranches({ limit: 1000 })
      .then((res) => setBranches(res.data?.data || res.data || []))
      .catch(console.error)
      .finally(() => setBranchesLoading(false));
  }, [branches.length, branchesLoading]);

  useEffect(() => {
    if (fundModal) {
      loadBranches();
      // Load the designated IBCT cash-drawer fund(s) so Fund Branch can debit the right source.
      fetchFunds()
        .then((res) => {
          const ibct = (res.data || []).filter((f) => f.isIBCTCashFund);
          setCashFunds(ibct);
          if (ibct.length === 1) fundForm.setFieldsValue({ sourceFundId: ibct[0]._id });
        })
        .catch(console.error);
    }
  }, [fundModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!initLoading && isHO) loadBranches();
  }, [initLoading, isHO]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchLedger = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pagination.pageSize };
      if (filters.entryType) params.entryType = filters.entryType;
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');

      const [res, cashRes] = await Promise.all([
        fetchCentralFund(params),
        fetchCashSummary(),
      ]);

      const data = res?.data;
      setEntries(data?.data || []);
      setCurrentBalance(data?.currentBalance ?? 0);
      setCashSummary(cashRes?.data?.data || null);

      setPagination((prev) => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load central fund ledger');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    if (!initLoading && isHO) fetchLedger(1);
  }, [filters, initLoading, isHO]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Branch cash drill-down ──────────────────────────────────────────────────
  const openBranchDrawer = async (branch) => {
    setDrawer({ open: true, branch });
    setDrawerData({ entries: [], loading: true, cashIn: 0, cashOut: 0, balance: 0 });
    try {
      const res = await fetchCashLedger({ branchId: branch.branchId, limit: 500 });
      const list = res?.data?.data || [];
      let cashIn = 0;
      let cashOut = 0;
      list.forEach((e) => {
        if (e.entryType === 'CASH_IN' || e.entryType === 'OPENING_BALANCE') cashIn += e.amount || 0;
        else if (e.entryType === 'CASH_OUT') cashOut += e.amount || 0;
      });
      // Latest entry (list is sorted newest-first) carries the current running balance.
      const balance = list.length ? (list[0].runningBalance || 0) : 0;
      setDrawerData({ entries: list, loading: false, cashIn, cashOut, balance });
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load branch cash ledger');
      setDrawerData({ entries: [], loading: false, cashIn: 0, cashOut: 0, balance: 0 });
    }
  };

  // ── Fund Branch ────────────────────────────────────────────────────────────
  const handleFundBranch = async (values) => {
    setFundSubmitting(true);

    const amount = DENOMINATION_NOTES.reduce(
      (sum, note) => sum + (fundDenominations[note.key] || 0) * note.value,
      0,
    );

    if (amount <= 0) {
      message.error('Total funding amount must be greater than 0');
      setFundSubmitting(false);
      return;
    }

    try {
      await fundBranch({
        branchId: values.branchId,
        amount,
        denominations: fundDenominations,
        sourceFundId: values.sourceFundId,
        description: values.description,
      });
      message.success('Branch funded successfully');
      setFundModal(false);
      fundForm.resetFields();
      setFundDenominations({});
      fetchLedger(1);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Branch funding failed');
    } finally {
      setFundSubmitting(false);
    }
  };

  const clearFilters = () => setFilters({ entryType: null, dateRange: null, branchId: null });
  const hasActiveFilters = filters.entryType || filters.branchId || filters.dateRange;

  // ── Branch cash breakdown table ──────────────────────────────────────────────
  const branchRows = (cashSummary?.summary || [])
    .slice()
    .sort((a, b) => (b.totalCash || 0) - (a.totalCash || 0));

  const branchCashColumns = [
    {
      title: 'Branch',
      dataIndex: 'branchName',
      render: (v, row) => (
        <Space size={6}>
          <ShopOutlined style={{ color: '#8c8c8c' }} />
          <span>
            <Text strong>{v}</Text>{' '}
            <Text type="secondary" style={{ fontSize: 12 }}>({row.branchCode})</Text>
          </span>
        </Space>
      ),
    },
    {
      title: <Tooltip title="Total cash sent to the branch (IBCT → Fund Branch)">Funded (In)</Tooltip>,
      dataIndex: 'funded',
      align: 'right',
      sorter: (a, b) => (a.funded || 0) - (b.funded || 0),
      render: (v) => <Text style={{ color: '#389e0d' }}>₹{fmt(v)}</Text>,
    },
    {
      title: <Tooltip title="Cash paid out to customers (walk-in / pledge settlements), net of reversals">Spent (Out)</Tooltip>,
      dataIndex: 'spent',
      align: 'right',
      sorter: (a, b) => (a.spent || 0) - (b.spent || 0),
      render: (v) => <Text style={{ color: '#cf1322' }}>₹{fmt(v)}</Text>,
    },
    {
      title: <Tooltip title="Net cash still physically held at the branch = Funded − Spent">Net Cash</Tooltip>,
      dataIndex: 'totalCash',
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.totalCash || 0) - (b.totalCash || 0),
      render: (v) => <Text strong style={{ color: v < 0 ? '#cf1322' : '#1d39c4' }}>₹{fmt(v)}</Text>,
    },
    {
      title: '',
      key: 'action',
      width: 90,
      align: 'center',
      render: (_, row) => (
        <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => openBranchDrawer(row)}>
          View
        </Button>
      ),
    },
  ];

  // ── Main ledger table columns (resizable) ────────────────────────────────────
  const baseColumns = [
    {
      title: 'Date', key: 'date', dataIndex: 'createdAt',
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Reference', key: 'reference', dataIndex: 'reference',
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Type', key: 'type', dataIndex: 'entryType',
      render: (v) => {
        const cfg = ENTRY_TYPE_CONFIG[v] || {};
        return <Tag color={cfg.color}>{cfg.label || v}</Tag>;
      },
    },
    {
      title: 'Description', key: 'description', dataIndex: 'description', ellipsis: true,
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Bank Ref', key: 'bankRef', dataIndex: 'bankReference',
      render: (v) => v ? <Text code style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Branch', key: 'branch', dataIndex: ['branch', 'name'],
      render: (v, row) => row.branch?.name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Credit', key: 'credit', dataIndex: 'amount', align: 'right',
      render: (v, row) =>
        CREDIT_TYPES.includes(row.entryType)
          ? <Text style={{ color: '#389e0d', fontWeight: 500 }}>₹{fmt(v)}</Text>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Debit', key: 'debit', dataIndex: 'amount', align: 'right',
      render: (v, row) =>
        DEBIT_TYPES.includes(row.entryType)
          ? <Text style={{ color: '#cf1322', fontWeight: 500 }}>₹{fmt(v)}</Text>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Balance', key: 'balance', dataIndex: 'runningBalance', align: 'right',
      render: (v) => (
        <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>₹{fmt(v)}</Text>
      ),
    },
  ];

  const columns = baseColumns.map((col) => ({
    ...col,
    width: colWidths[col.key],
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(col.key),
    }),
  }));

  const tableMinWidth = Object.values(colWidths).reduce((s, w) => s + w, 0);

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  if (!isHO) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <LockOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5} type="secondary">Central Fund Ledger</Title>
        <Text type="secondary">This section is restricted to Head Office users.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Resize-handle styling (scoped to this page's tables). */}
      <style>{`
        .cfl-resize-handle {
          position: absolute; right: -4px; top: 0; bottom: 0;
          width: 10px; cursor: col-resize; z-index: 1;
          touch-action: none;
        }
        .cfl-resize-handle::after {
          content: ''; position: absolute; right: 4px; top: 25%; bottom: 25%;
          width: 2px; border-radius: 2px; background: transparent;
          transition: background .15s;
        }
        .cfl-ledger-table th:hover .cfl-resize-handle::after { background: #91caff; }
        .cfl-ledger-table .ant-table-cell { white-space: nowrap; }
      `}</style>

      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <BankOutlined style={{ marginRight: 8 }} />
            Central Fund Ledger
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Head Office — Bank &amp; Cash funds</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              Load funds from <b>Company Accounting → Manage Company Funds</b> (Load on the Central Fund). This ledger is now read-only for credits.
            </Text>
          </div>
        </Col>
        <Col>
          <Space>
            <Button type="default" icon={<BankOutlined />} onClick={() => setFundModal(true)}>
              Fund Branch
            </Button>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => fetchLedger(pagination.current)} loading={loading} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {/* ── KPI cards ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title={<span><BankOutlined /> Central Fund (Bank)</span>}
              value={fmt(currentBalance)}
              prefix="₹"
              valueStyle={{ fontSize: 20, fontWeight: 'bold', color: currentBalance < 0 ? '#cf1322' : '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bordered style={{ background: '#f9f0ff' }}>
            <Statistic
              title={<span><WalletOutlined /> Net Cash at Branches</span>}
              value={fmt(cashSummary?.totalFunded || 0)}
              prefix="₹"
              valueStyle={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title={<span><ArrowUpOutlined /> Total Funded to Branches</span>}
              value={fmt(cashSummary?.totalFundedGross || 0)}
              prefix="₹"
              valueStyle={{ fontSize: 18, color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title={<span><ArrowDownOutlined /> Total Spent by Branches</span>}
              value={fmt(cashSummary?.totalSpent || 0)}
              prefix="₹"
              valueStyle={{ fontSize: 18, color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Branch cash breakdown ── */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <WalletOutlined />
            <span>Cash at Branches</span>
            <Tooltip title="Net cash still physically held at each branch = cash funded (IBCT → Fund Branch) minus cash spent on walk-in / pledge customer payouts (net of reversals). Click View for a full breakdown of a branch's cash drawer.">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
        extra={<Text type="secondary" style={{ fontSize: 12 }}>{branchRows.length} branch{branchRows.length === 1 ? '' : 'es'}</Text>}
      >
        {cashSummary ? (
          <Table
            dataSource={branchRows}
            columns={branchCashColumns}
            rowKey="branchId"
            size="small"
            pagination={false}
            scroll={{ y: 260, x: 'max-content' }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No cash funded to branches yet" /> }}
            summary={(rows) => {
              const t = rows.reduce(
                (acc, r) => ({
                  funded: acc.funded + (r.funded || 0),
                  spent: acc.spent + (r.spent || 0),
                  totalCash: acc.totalCash + (r.totalCash || 0),
                }),
                { funded: 0, spent: 0, totalCash: 0 },
              );
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0}><Text strong>All Branches</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right"><Text strong style={{ color: '#389e0d' }}>₹{fmt(t.funded)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><Text strong style={{ color: '#cf1322' }}>₹{fmt(t.spent)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><Text strong style={{ color: t.totalCash < 0 ? '#cf1322' : '#1d39c4' }}>₹{fmt(t.totalCash)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        ) : (
          <Spin size="small" />
        )}
      </Card>

      <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>Ledger Entries</Text>
      </Divider>

      {/* ── Filters ── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={12} md={7}>
            <Select
              placeholder="All branches"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={filters.branchId}
              loading={branchesLoading}
              onChange={(v) => setFilters((f) => ({ ...f, branchId: v || null }))}
              options={branches.map((b) => ({ value: b._id, label: `${b.name} (${b.code})` }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="All entry types"
              allowClear
              style={{ width: '100%' }}
              value={filters.entryType}
              onChange={(v) => setFilters((f) => ({ ...f, entryType: v || null }))}
              options={ENTRY_TYPE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={16} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters((f) => ({ ...f, dateRange: dates }))}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={8} md={3}>
            <Button icon={<ClearOutlined />} onClick={clearFilters} disabled={!hasActiveFilters} block>
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ── Ledger table (resizable columns — drag the right edge of any header) ── */}
      <Table
        className="cfl-ledger-table"
        dataSource={entries}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        rowKey="_id"
        loading={loading}
        size="small"
        bordered
        scroll={{ x: Math.max(tableMinWidth, 1100) }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `${t} entries`,
          onChange: (page) => fetchLedger(page),
        }}
        locale={{ emptyText: 'No central fund entries found' }}
      />

      {/* ── Branch cash drill-down Drawer ── */}
      <Drawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, branch: null })}
        width={720}
        title={
          <Space>
            <ShopOutlined />
            <span>{drawer.branch ? `${drawer.branch.branchName} (${drawer.branch.branchCode})` : 'Branch'} — Cash Drawer</span>
          </Space>
        }
      >
        {drawer.branch && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={8}>
                <Card size="small" bordered>
                  <Statistic title={<span><ArrowUpOutlined /> Cash In</span>} value={fmt(drawerData.cashIn)} prefix="₹" valueStyle={{ color: '#389e0d', fontSize: 18 }} />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" bordered>
                  <Statistic title={<span><ArrowDownOutlined /> Cash Out</span>} value={fmt(drawerData.cashOut)} prefix="₹" valueStyle={{ color: '#cf1322', fontSize: 18 }} />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" bordered style={{ background: '#f9f0ff' }}>
                  <Statistic title={<span><WalletOutlined /> Current Cash Balance</span>} value={fmt(drawerData.balance)} prefix="₹" valueStyle={{ color: '#722ed1', fontSize: 18, fontWeight: 'bold' }} />
                </Card>
              </Col>
            </Row>

            <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Cash Ledger Entries</Text>
            </Divider>

            <Table
              dataSource={drawerData.entries}
              rowKey="_id"
              loading={drawerData.loading}
              size="small"
              bordered
              pagination={{ pageSize: 15, showTotal: (t) => `${t} entries`, size: 'small' }}
              scroll={{ x: 640 }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No cash entries for this branch" /> }}
              columns={[
                { title: 'Date', dataIndex: 'createdAt', width: 100, render: (v) => v ? dayjs(v).format('DD/MM/YY') : '—' },
                {
                  title: 'Type', dataIndex: 'entryType', width: 110,
                  render: (v) => {
                    const cfg = CASH_LEDGER_TYPE_CONFIG[v] || { color: 'default', label: v };
                    return <Tag color={cfg.color}>{cfg.label}</Tag>;
                  },
                },
                { title: 'Description', dataIndex: 'description', ellipsis: true, render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : <Text type="secondary">—</Text> },
                {
                  title: 'In', dataIndex: 'amount', width: 110, align: 'right',
                  render: (v, row) => ['CASH_IN', 'OPENING_BALANCE'].includes(row.entryType)
                    ? <Text style={{ color: '#389e0d' }}>₹{fmt(v)}</Text> : <Text type="secondary">—</Text>,
                },
                {
                  title: 'Out', dataIndex: 'amount', width: 110, align: 'right',
                  render: (v, row) => row.entryType === 'CASH_OUT'
                    ? <Text style={{ color: '#cf1322' }}>₹{fmt(v)}</Text> : <Text type="secondary">—</Text>,
                },
                {
                  title: 'Balance', dataIndex: 'runningBalance', width: 120, align: 'right',
                  render: (v) => <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>₹{fmt(v)}</Text>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* ── Fund Branch Modal ── */}
      <Modal
        title={<><BankOutlined style={{ marginRight: 8 }} />Fund Branch</>}
        open={fundModal}
        onCancel={() => { setFundModal(false); fundForm.resetFields(); setFundDenominations({}); }}
        onOk={() => fundForm.submit()}
        okText="Transfer Funds"
        confirmLoading={fundSubmitting}
        destroyOnClose
        width={550}
      >
        <Form form={fundForm} layout="vertical" onFinish={handleFundBranch} style={{ marginTop: 16 }}>
          <Form.Item
            name="sourceFundId"
            label="Source Cash Fund (IBCT drawer)"
            tooltip="The physical-cash drawer this branch funding is paid out of. Its balance and denomination inventory are reduced."
            extra={cashFunds.length === 0 ? 'No IBCT Cash Fund configured — funding will record allocation without debiting a cash drawer.' : undefined}
          >
            <Select
              placeholder={cashFunds.length ? 'Select the cash drawer fund' : 'No IBCT Cash Fund configured'}
              allowClear
              disabled={cashFunds.length === 0}
            >
              {cashFunds.map((f) => (
                <Select.Option key={f._id} value={f._id}>
                  {f.name} (₹{fmt(f.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="branchId"
            label="Select Branch"
            rules={[{ required: true, message: 'Branch is required' }]}
          >
            <Select placeholder="Select target branch" loading={branchesLoading} showSearch optionFilterProp="children">
              {branches.map((b) => (
                <Select.Option key={b._id} value={b._id}>
                  {b.name} ({b.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Denominations</Text>
            <div style={{ marginTop: 8, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
              <DenominationCountForm counts={fundDenominations} onChange={setFundDenominations} />
            </div>
          </div>

          <Form.Item name="businessDate" label="Business Date">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Optional description" maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
